import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Account } from '@tcs/client-portal-api/types/Account';
import { SelectorItem } from '@tcs/common/components/atoms/Selector';
import Spinner from '@tcs/common/components/atoms/Spinner';
import SubTitleSelector from '@tcs/common/components/molecules/SubTitleSelector';
import Header, { HeaderProps } from '.';
import { useStyles } from './style';
import { Banner, Typography } from '@forgeglobal/design-framework';
import formatDate from '@tcs/common/utils/formatDate';

type AccountsWithNameHeaderProps = HeaderProps & {
  accounts: Account[];
  loading: boolean;
};

export const AccountsWithNameHeader = ({
  accounts,
  loading,
  ...props
}: AccountsWithNameHeaderProps) => {
  const classes = useStyles();
  const nav = useNavigate();
  const location = useLocation();
  const params = useParams<{ accountNumber: string }>();

  const listItem = (
    accountNumber: string,
    displayAccountType: string,
    status: string
  ) => {
    return (
      <div className={classes.itemInFunction}>
        <div className={classes.displayAccountType}>
          {displayAccountType ?? ''}
        </div>
        <div className={classes.accountNumber}>{accountNumber ?? ''}</div>
        {status && (status === 'Closed' || status === 'Void') && (
          <div className={classes.status}>{status ? 'Closed' : ''}</div>
        )}
      </div>
    );
  };

  const selectorItems: SelectorItem[] = useMemo(() => {
    if (accounts == null) {
      return [];
    }
    return accounts.map(
      ({ accountNumber, displayAccountType, status }: Account) => ({
        value: accountNumber,
        listItem: listItem(accountNumber, displayAccountType, status),
      })
    );
  }, [accounts]);

  const onSelectAccount = useCallback(
    (accountNumber: string) => {
      const path = location.pathname.replace(
        /\/accounts\/[^/]+/,
        `/accounts/${accountNumber}`
      );
      nav(path);
    },
    [nav, location]
  );

  if (loading) {
    return <Spinner />;
  }

  // we could check how long ago the account was closed but it doesn't matter
  // they have successfully logged in so we need to show a closed banner
  const accountIsClosed = accounts.filter(
    (a) => a.accountNumber === params.accountNumber && a.status === 'Closed'
  );

  const accountIsVoid = accounts.filter(
    (a) => a.accountNumber === params.accountNumber && a.status === 'Void'
  );

  const closedDate =
    accountIsClosed.length > 0 ? accountIsClosed[0].closedDate : null;

  const customBanner = (text: string) => (
    <Banner severity="warning">
      <Typography
        variant="body2"
        color="textPrimary"
        data-test-id="login-message">
        {text}
      </Typography>
    </Banner>
  );

  return (
    <div>
      {accountIsClosed.length > 0 &&
        customBanner(
          `This account was closed on ${formatDate(
            closedDate
          )}. Tax documents and account statements are still available.`
        )}
      {accountIsVoid.length > 0 &&
        customBanner(
          'This account was closed due to inactivity. Please reach out to customer service if you have any questions.'
        )}
      {props.pageTitle !== '' && (
        <Header tabs={props.tabs}>
          <div className={classes.accountsHeader}>
            <SubTitleSelector
              title={props.pageTitle}
              items={selectorItems}
              selectedValue={params.accountNumber}
              onSelect={onSelectAccount}
            />
          </div>
        </Header>
      )}
    </div>
  );
};

export default AccountsWithNameHeader;
